import * as React from 'react';
import { HiChevronDown, HiChevronLeft, HiChevronRight } from 'react-icons/hi2';
import { DayPicker, getDefaultClassNames, type Modifiers } from 'react-day-picker';
import { cn } from '@/lib/utils/cn';
import { stanAnalytics } from '@/lib';
import { useUser } from '@/queries/useUser';
import 'react-day-picker/src/style.css';
import { StanCalendarDayButton } from './Button/StanCalendarDayButton';
import buttonStyles from './Button/StanCalendarDayButton.module.css';
import styles from './StanCalendar.module.css';

/**
 * StanCalendar - A customizable calendar component built on react-day-picker
 *
 * @example
 * // Single date selection
 * <StanCalendar
 *   analyticsName="booking-calendar"
 *   mode="single"
 *   selected={selectedDate}
 *   onSelect={setSelectedDate}
 * />
 *
 * @example
 * // Date range selection
 * <StanCalendar
 *   analyticsName="date-range-picker"
 *   mode="range"
 *   selected={dateRange}
 *   onSelect={setDateRange}
 * />
 */
function StanCalendar({
    analyticsName,
    className,
    showOutsideDays = false,
    captionLayout = 'label',
    animate = true,
    min,
    max,
    ...props
}: React.ComponentProps<typeof DayPicker> & {
    analyticsName: string;
    min?: number;
    max?: number;
}) {
    const defaultClassNames = getDefaultClassNames();
    const { user } = useUser();

    const handleSelect = (
        selected: any,
        triggerDate: Date,
        modifiers: Modifiers,
        e: React.MouseEvent | React.KeyboardEvent
    ) => {
        stanAnalytics('admin-calendar-select', {
            meta: {
                user_id: user?.user_id,
                username: user?.username,
            },
            props: {
                calendar_name: analyticsName,
                selection_mode: props.mode || 'single',
            },
        });

        if ('onSelect' in props && props.onSelect) {
            props.onSelect(selected, triggerDate, modifiers, e);
        }
    };

    return (
        <DayPicker
            min={min}
            max={max}
            showOutsideDays={showOutsideDays}
            className={cn(styles.calendar, className)}
            captionLayout={captionLayout}
            onSelect={handleSelect}
            animate={animate}
            classNames={{
                root: cn(styles.calendar, defaultClassNames.root),
                nav: cn(styles.nav, defaultClassNames.nav),
                months: cn(styles.months, defaultClassNames.months),
                month: cn(styles.month, defaultClassNames.month),
                week: cn(styles.week, defaultClassNames.week),
                month_caption: cn(styles.monthCaption, defaultClassNames.month_caption),
                dropdowns: cn(styles.dropdowns, defaultClassNames.dropdowns),
                dropdown_root: cn(styles.dropdownRoot, defaultClassNames.dropdown_root),
                dropdown: cn(styles.dropdown, defaultClassNames.dropdown),
                caption_label: cn(styles.captionLabel, defaultClassNames.caption_label),
                button_previous: cn(styles.chevronButton, defaultClassNames.button_previous),
                button_next: cn(styles.chevronButton, defaultClassNames.button_next),
                chevron: cn(styles.chevron, defaultClassNames.chevron),
                table: styles.table,
                weekdays: cn(styles.weekdays, defaultClassNames.weekdays),
                weekday: cn(styles.weekday, buttonStyles.weekday, defaultClassNames.weekday),
                day: cn(styles.day, defaultClassNames.day),
                day_button: cn(styles.dayButton),
                range_start: cn(styles.rangeStart),
                range_middle: cn(styles.rangeMiddle),
                range_end: cn(styles.rangeEnd),
                ...props.classNames,
            }}
            formatters={{
                formatWeekdayName: weekday => {
                    return weekday.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 1);
                },
                formatMonthDropdown: date => date.toLocaleString('default', { month: 'short' }),
            }}
            components={{
                Root: ({ className, rootRef, ...componentProps }) => {
                    return (
                        <div
                            data-slot='calendar'
                            ref={rootRef}
                            className={cn('', className)}
                            role='application'
                            aria-label={`Calendar for ${analyticsName}`}
                            {...componentProps}
                        />
                    );
                },
                Chevron: ({ className, orientation, ...componentProps }) => {
                    if (orientation === 'left') {
                        return <HiChevronLeft className={cn(styles.chevron, className)} {...componentProps} />;
                    }
                    if (orientation === 'right') {
                        return <HiChevronRight className={cn(styles.chevron, className)} {...componentProps} />;
                    }
                    return <HiChevronDown className={cn(styles.chevron, className)} {...componentProps} />;
                },
                ButtonPrevious: ({ ...componentProps }) => {
                    return (
                        <button
                            aria-label='Go to previous month'
                            className={cn(styles.chevronButton)}
                            {...componentProps}
                            type='button'
                        />
                    );
                },
                ButtonNext: ({ ...componentProps }) => {
                    return (
                        <button
                            aria-label='Go to next month'
                            className={cn(styles.chevronButton)}
                            {...componentProps}
                            type='button'
                        />
                    );
                },
                DayButton: StanCalendarDayButton,
                WeekNumber: ({ children, ...componentProps }) => {
                    return (
                        <td {...componentProps}>
                            <div className={styles.weekNumber}>{children}</div>
                        </td>
                    );
                },
            }}
            {...(props as any)}
        />
    );
}

StanCalendar.displayName = 'StanCalendar';

export { StanCalendar };
